import { Box } from "@mui/material";
import { FieldErrors, UseFormRegister } from "react-hook-form";

import { SingleSelectInput } from "../../components/SelectInput";
import Input from "../../components/UserInput/Input";
import { Prefix } from "../../utils/constants";
import { newUserSchema } from "../../yup";
import style from "./admin.module.css";

/**
 * Form value shape for admin create/edit flows.
 *
 * Notes:
 * - Derived from `newUserSchema` to keep validation and types in sync.
 * - TODO: Confirm whether any optional fields differ between add vs edit.
 */
export type AdminFormValues = typeof newUserSchema.__outputType;

type AdminFormFieldsProps = {
  register: UseFormRegister<AdminFormValues>;
  errors: FieldErrors<AdminFormValues>;
  prefixValue?: string;
  disableEmail?: boolean;
  phoneRequired?: boolean;
};

/**
 * Renders shared admin form fields for prefix, name, email, and phone.
 *
 * Args:
 *   register (UseFormRegister<AdminFormValues>): react-hook-form register function.
 *   errors (FieldErrors<AdminFormValues>): validation errors for form fields.
 *   prefixValue (string, optional): current prefix value for controlled select.
 *   disableEmail (boolean, optional): disables the email input when true.
 *
 * Returns:
 *   JSX.Element: Form field rows for admin details.
 *
 * Notes:
 *   Email is required by schema, but can be read-only in edit flows.
 */
const AdminFormFields = ({
  register,
  errors,
  prefixValue,
  disableEmail = false,
  phoneRequired = false,
}: AdminFormFieldsProps) => {
  const selectValueProps =
    prefixValue === undefined ? {} : { value: prefixValue };

  return (
    <>
      <Box className={style.row}>
        <SingleSelectInput
          placeholder="Select Prefix"
          label="Prefix"
          register={register}
          name="prefix"
          errors={errors}
          data={Prefix}
          {...selectValueProps}
        />
      </Box>

      <Box className={style.row}>
        <Input
          placeholder="First Name"
          label="First Name"
          required
          register={register}
          name="given_name"
          errors={errors}
        />
        <Input
          placeholder="Last Name"
          label="Last Name"
          required
          register={register}
          name="surname"
          errors={errors}
        />
      </Box>

      <Box className={style.row}>
        <Input
          placeholder="Email Address"
          label="Email Address"
          required
          register={register}
          name="mail"
          errors={errors}
          disabled={disableEmail}
        />
        <Input
          placeholder="Phone Number"
          label="Phone Number"
          required={phoneRequired}
          register={register}
          name="phone"
          errors={errors}
        />
      </Box>
    </>
  );
};

export default AdminFormFields;
